import { useEffect, useState } from 'react';
import * as auth from './auth';
import * as fmt from './format';
import { useStore } from './store';
import { Features } from './views/Features';
import { History } from './views/History';
import { MapView } from './views/MapView';
import { Manage } from './views/Manage';
import { Progress } from './views/Progress';
import { Routes } from './views/Routes';
import { Shared } from './views/Shared';
import { Today } from './views/Today';

const TABS = ['Today', 'Map', 'Progress', 'History', 'Routes', 'Manage'] as const;
type Tab = (typeof TABS)[number];

/// Read once at module load rather than on every render: it cannot change
/// without a navigation, which reloads the app.
const isFeaturePage = window.location.pathname.replace(/\/+$/, '') === '/features';

/// A shared status link, `/shared/<token>`. Read at module load for the same
/// reason: the path cannot change without a navigation. The token is the whole
/// credential, so nothing here checks it — the server decides whether it is
/// valid, and what a holder of it is allowed to see.
const sharedToken = window.location.pathname
  .replace(/\/+$/, '')
  .match(/^\/shared\/([A-Za-z0-9_-]+)$/)?.[1] ?? null;

/// The tab named by the URL fragment, or Today.
///
/// Kept in the fragment rather than the path because the site is a static
/// bundle behind a CloudFront rewrite: every unknown path already becomes
/// index.html, so a real path would work but would also need the rewrite to
/// keep agreeing with this list. A fragment is never sent to the server and
/// cannot fall out of step with it.
function tabFromHash(): Tab {
  const name = window.location.hash.replace(/^#/, '').toLowerCase();
  return TABS.find((tab) => tab.toLowerCase() === name) ?? 'Today';
}

export function App() {
  const [signedIn, setSignedIn] = useState(auth.isSignedIn());
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(tabFromHash);
  // Not on the public page: it shows nothing from the API, so fetching would
  // be a round trip nobody reads — and a needless failure to render if the
  // token has expired.
  const store = useStore(signedIn && !isFeaturePage && sharedToken === null);

  // Back and forward, and anyone editing the fragment by hand.
  useEffect(() => {
    const follow = () => setTab(tabFromHash());
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);

  // Consume the ?code= redirect before anything tries to call the API.
  useEffect(() => {
    void (async () => {
      try {
        if (await auth.completeSignIn()) setSignedIn(true);
      } catch (failure) {
        setAuthError(failure instanceof Error ? failure.message : String(failure));
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // Before the gate on purpose: the feature page needs no account. CloudFront
  // already rewrites unknown paths to index.html, which is what makes this a
  // real path rather than a hash.
  if (isFeaturePage) return <Features />;
  // Also before the gate, and for the same reason: whoever opens this has no
  // account, which is the point.
  if (sharedToken) return <Shared token={sharedToken} />;

  if (!ready) return <main className="shell"><p className="muted">Loading…</p></main>;

  if (!signedIn) {
    return (
      <main className="shell shell--centred">
        <h1>Steps</h1>
        <p className="muted">Track your virtual walk</p>
        <button className="primary" onClick={() => void auth.beginSignIn()}>Sign in</button>
        {authError && <p className="error">{authError}</p>}
        <p className="muted"><a href="/features">What Steps does</a></p>
      </main>
    );
  }

  return (
    <main className="shell">
      <header className="shell__head">
        <div>
          <h1>{store.walk?.name ?? 'Steps'}</h1>
          {store.summary && (
            <p className="muted">
              {fmt.percent(store.summary.pctComplete)} · day {store.summary.daysElapsed}
            </p>
          )}
        </div>
        <div className="shell__actions">
          <button onClick={() => void store.reload()} disabled={store.loading}>
            {store.loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button onClick={() => { auth.signOut(); setSignedIn(false); }}>Sign out</button>
        </div>
      </header>

      <nav className="tabs">
        {TABS.map((name) => (
          <a
            key={name}
            href={`#${name.toLowerCase()}`}
            className={tab === name ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
            aria-current={tab === name ? 'page' : undefined}
          >
            {name}
          </a>
        ))}
      </nav>

      {store.error && <p className="error">{store.error}</p>}

      {tab === 'Today' && <Today store={store} />}
      {tab === 'Map' && <MapView store={store} />}
      {tab === 'Progress' && <Progress store={store} />}
      {tab === 'History' && <History store={store} />}
      {tab === 'Routes' && <Routes />}
      {tab === 'Manage' && <Manage store={store} />}
    </main>
  );
}
